import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { NetworkProbeResult } from '@/types'
import { useDesignTokens } from '@/hooks/useDesignTokens'
import {
  colorBackgroundContainerContent,
  colorBorderDividerDefault,
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorTextStatusSuccess,
  colorTextStatusError,
  colorTextStatusWarning,
} from '@cloudscape-design/design-tokens'

interface Props { probes: NetworkProbeResult[] }

function StatusIcon({ status }: { status: 'ok' | 'failed' | 'timeout' | 'skipped' }) {
  switch (status) {
    case 'ok': return <span style={{ color: colorTextStatusSuccess, fontWeight: 700 }}>✓</span>
    case 'failed': return <span style={{ color: colorTextStatusError, fontWeight: 700 }}>✗</span>
    case 'timeout': return <span style={{ color: colorTextStatusWarning, fontWeight: 700 }}>⏱</span>
    case 'skipped': return <span style={{ color: colorTextBodySecondary }}>—</span>
  }
}

export function NetworkWaterfall({ probes }: Props) {
  const tokens = useDesignTokens()
  if (probes.length === 0) return <div style={styles.empty}>Run a benchmark to see network diagnostics</div>

  const hasDiagnostics = probes.some(p => p.diagnostic)
  const hasTimingData = probes.some(p => p.dnsLookupMs > 0 || p.tcpConnectMs > 0 || p.tlsHandshakeMs > 0)

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Network Connection Diagnostics</h3>

      {/* Diagnostic status table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Endpoint</th>
            <th style={styles.th}>DNS</th>
            <th style={styles.th}>TCP</th>
            <th style={styles.th}>TLS</th>
            {hasDiagnostics && <th style={styles.th}>Connection Setup</th>}
            {hasDiagnostics && <th style={styles.th}>HTTP Round-trip</th>}
            <th style={styles.th}>Total</th>
            <th style={styles.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {probes.map((p, i) => {
            const d = p.diagnostic
            const hasError = p.error || d?.errorType
            return (
              <tr key={i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td style={styles.td}>
                  <code style={{ fontSize: 11 }}>{p.host}</code>
                </td>
                <td style={styles.td}>
                  {d ? <StatusIcon status={d.dnsStatus} /> : (p.dnsLookupMs > 0 ? `${Math.round(p.dnsLookupMs)}ms` : '—')}
                  {d?.dnsStatus === 'ok' && d.connectionSetupMs > 0 && <span style={styles.timing}> ~{Math.round(d.connectionSetupMs * 0.3)}ms</span>}
                </td>
                <td style={styles.td}>
                  {d ? <StatusIcon status={d.tcpStatus} /> : (p.tcpConnectMs > 0 ? `${Math.round(p.tcpConnectMs)}ms` : '—')}
                  {d?.tcpStatus === 'ok' && d.connectionSetupMs > 0 && <span style={styles.timing}> ~{Math.round(d.connectionSetupMs * 0.2)}ms</span>}
                </td>
                <td style={styles.td}>
                  {d ? <StatusIcon status={d.tlsStatus} /> : (p.tlsHandshakeMs > 0 ? `${Math.round(p.tlsHandshakeMs)}ms` : '—')}
                  {d?.tlsStatus === 'ok' && d.connectionSetupMs > 0 && <span style={styles.timing}> ~{Math.round(d.connectionSetupMs * 0.5)}ms</span>}
                </td>
                {hasDiagnostics && (
                  <td style={styles.td}>
                    {d ? `${d.connectionSetupMs}ms` : '—'}
                  </td>
                )}
                {hasDiagnostics && (
                  <td style={styles.td}>
                    {d?.httpRoundTripMs ? `${d.httpRoundTripMs}ms` : '—'}
                  </td>
                )}
                <td style={styles.td}>
                  <strong>{Math.round(p.totalMs)}ms</strong>
                </td>
                <td style={styles.td}>
                  {hasError ? (
                    <span style={{ color: colorTextStatusError, fontSize: 11 }}>{d?.errorType || p.error}</span>
                  ) : (
                    <span style={{ color: colorTextStatusSuccess }}>Connected</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Bar chart for timing visualization (only if we have real timing data) */}
      {hasTimingData && (
        <>
          <h4 style={{ ...styles.title, marginTop: 16 }}>Timing Waterfall (ms)</h4>
          <ResponsiveContainer width="100%" height={Math.max(120, probes.length * 60 + 60)}>
            <BarChart
              data={probes.map(p => ({
                host: p.host.length > 30 ? p.host.slice(0, 30) + '...' : p.host,
                DNS: Math.round(p.dnsLookupMs),
                TCP: Math.round(p.tcpConnectMs),
                TLS: Math.round(p.tlsHandshakeMs),
              }))}
              layout="vertical"
              margin={{ left: 10, right: 20 }}
            >
              <XAxis type="number" stroke={tokens.axisStroke} />
              <YAxis type="category" dataKey="host" width={220} tick={{ fill: tokens.axisLabelColor, fontSize: 12 }} />
              <Tooltip
                contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}` }}
                labelStyle={{ color: colorTextBodyDefault }}
                formatter={(value: number) => `${value}ms`}
              />
              <Legend />
              <Bar dataKey="DNS" stackId="a" fill={tokens.seriesPalette[0]} />
              <Bar dataKey="TCP" stackId="a" fill={tokens.seriesPalette[1]} />
              <Bar dataKey="TLS" stackId="a" fill={tokens.seriesPalette[2]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {hasDiagnostics && (
        <div style={styles.note}>
          Local mode: DNS/TCP/TLS times are estimated from connection setup overhead (cold vs warm request delta).
          Cloud mode provides exact per-layer timing from the Lambda.
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { backgroundColor: colorBackgroundContainerContent, borderRadius: 8, border: `1px solid ${colorBorderDividerDefault}`, padding: 16, marginBottom: 16 },
  title: { color: colorTextBodyDefault, margin: '0 0 12px', fontSize: 15 },
  empty: { color: colorTextBodySecondary, fontSize: 13, padding: 20, textAlign: 'center' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 12px', borderBottom: `2px solid ${colorBorderDividerDefault}`, color: colorTextBodySecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const },
  td: { padding: '8px 12px', borderBottom: `1px solid ${colorBorderDividerDefault}`, color: colorTextBodyDefault },
  rowEven: {},
  rowOdd: { backgroundColor: 'rgba(255,255,255,0.02)' },
  timing: { color: colorTextBodySecondary, fontSize: 11 },
  note: { color: colorTextBodySecondary, fontSize: 11, marginTop: 12, fontStyle: 'italic' as const },
}
